/**
 * CountyFAQ Component
 * Accordion-style FAQ section for county-specific questions
 */

import { useState } from 'react';
import type { CountyFAQ as CountyFAQType } from '../../types/county';

interface CountyFAQProps {
  faqs: CountyFAQType[];
  countyName: string;
}

interface FAQAccordionItemProps {
  faq: CountyFAQType;
  isOpen: boolean;
  onClick: () => void;
}

function FAQAccordionItem({ faq, isOpen, onClick }: FAQAccordionItemProps) {
  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <button
        onClick={onClick}
        className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-neutral-50 transition-colors text-left"
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${faq.question}`}
      >
        <span className="font-medium text-neutral-900 pr-4">{faq.question}</span>
        <svg
          className={`w-5 h-5 text-neutral-500 flex-shrink-0 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div
          id={`faq-answer-${faq.question}`}
          className="px-6 py-4 bg-neutral-50 border-t border-neutral-200"
        >
          <div className="text-neutral-700 leading-relaxed whitespace-pre-line">
            {faq.answer}
          </div>
        </div>
      )}
    </div>
  );
}

export function CountyFAQ({ faqs, countyName }: CountyFAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Get unique categories
  const categories = Array.from(
    new Set(faqs.map(faq => faq.category).filter(Boolean))
  ).sort();

  // Filter FAQs by category
  const filteredFAQs =
    categoryFilter === 'all'
      ? faqs
      : faqs.filter(faq => faq.category === categoryFilter);

  return (
    <section id="faq" className="py-12 bg-white border-b border-neutral-200">
      <div className="container mx-auto px-4 max-w-4xl">
        <h2 className="text-3xl font-bold text-neutral-900 mb-3">
          {countyName} County FAQ
        </h2>
        <p className="text-neutral-600 mb-6">
          Frequently asked questions specific to waste management requirements in {countyName} County
        </p>

        {/* Category Filter (if categories exist) */}
        {categories.length > 0 && (
          <div className="mb-6">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  setCategoryFilter('all');
                  setOpenIndex(null);
                }}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  categoryFilter === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                All Questions
              </button>
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => {
                    setCategoryFilter(category!);
                    setOpenIndex(null);
                  }}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors capitalize ${
                    categoryFilter === category
                      ? 'bg-primary-600 text-white'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* FAQ List */}
        <div className="space-y-3">
          {filteredFAQs.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">
              No questions found in this category
            </div>
          ) : (
            filteredFAQs.map((faq, index) => (
              <FAQAccordionItem
                key={index}
                faq={faq}
                isOpen={openIndex === index}
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              />
            ))
          )}
        </div>

        {/* Contact Section */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            Still have questions?
          </h3>
          <p className="text-blue-800 mb-4">
            Can&apos;t find what you&apos;re looking for? Contact your local building department or our support team for assistance.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href="/docs/faq"
              className="inline-block px-4 py-2 bg-white text-primary-600 border border-primary-300 font-medium rounded-lg hover:bg-primary-50 transition-colors"
            >
              View General FAQ
            </a>
            <a
              href="mailto:support@calwmp.com"
              className="inline-block px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
